// Het wiegje: de keuzes van de gast, bewaard in deze browser tot het afrekenen.
import type { GuestGift } from '../shared/api-types.ts';

export type CartLine = { giftId: string; amountCents: number };

const STORAGE_KEY = 'wiegje';

function isCartLine(x: unknown): x is CartLine {
  if (typeof x !== 'object' || x === null) return false;
  const line = x as Record<string, unknown>;
  return (
    typeof line.giftId === 'string' &&
    typeof line.amountCents === 'number' &&
    Number.isSafeInteger(line.amountCents) &&
    line.amountCents > 0
  );
}

export function loadCart(): CartLine[] {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '[]');
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isCartLine).map((l) => ({ giftId: l.giftId, amountCents: l.amountCents }));
  } catch {
    return [];
  }
}

export function saveCart(lines: CartLine[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(lines));
  } catch {
    // Privévenster of volle opslag: het wiegje leeft dan alleen in het geheugen.
  }
}

/**
 * Houdt alleen wat nog kan: een cadeau dat niemand koos (bedrag = de actuele prijs),
 * of een potje dat nog niet vol is (bedrag hoogstens wat nog nodig is).
 */
export function cleanCart(
  lines: CartLine[],
  gifts: GuestGift[],
): { kept: CartLine[]; droppedTitles: string[] } {
  const byId = new Map(gifts.map((g) => [g.id, g]));
  const kept: CartLine[] = [];
  const droppedTitles: string[] = [];
  for (const line of lines) {
    const gift = byId.get(line.giftId);
    if (!gift) {
      droppedTitles.push('een cadeau');
    } else if (gift.kind === 'cadeau') {
      if (gift.taken) droppedTitles.push(gift.title);
      else kept.push({ giftId: gift.id, amountCents: gift.priceCents });
    } else if (gift.remainingCents === null) {
      kept.push({ giftId: gift.id, amountCents: line.amountCents });
    } else if (gift.remainingCents <= 0) {
      droppedTitles.push(gift.title);
    } else {
      kept.push({ giftId: gift.id, amountCents: Math.min(line.amountCents, gift.remainingCents) });
    }
  }
  return { kept, droppedTitles };
}

export function cartTotal(lines: CartLine[]): number {
  return lines.reduce((sum, l) => sum + l.amountCents, 0);
}

export function withoutGift(lines: CartLine[], giftId: string): CartLine[] {
  return lines.filter((l) => l.giftId !== giftId);
}

/** Zet een lijn in het wiegje of vervangt het bedrag van een bestaande lijn. */
export function withLine(lines: CartLine[], line: CartLine): CartLine[] {
  return [...withoutGift(lines, line.giftId), line];
}
